import { useCallback, useEffect, useMemo } from 'react';
import { Group, Switch, createStyles, Flex, Box } from '@mantine/core';
import type { ZodTypeAny } from 'zod';
import { z } from 'zod';
import { IconLock } from '@tabler/icons-react';

import dt from '~/domains/contest/domains/manage/domains/common/testing.const';
import { Text } from '~/domains/design-system/Text';

import type { BaseInterativeField, FieldDescriptor, StaticFieldProps } from '../../types/common';
import { getDependenciesValues } from '../../../context/utils/getDependencyValues';
import { getIsDependenciesSatisfied } from '../../../context/utils/getIsDependenciesSatisfied';

export type SwitchSummaryFieldType = BaseInterativeField & {
  type: 'SwitchSummaryField';
};

const useStyles = createStyles((theme) => ({
  card: {
    width: '100%',
  },
  justify: {
    minHeight: 30,
    justifyContent: 'space-between',
  },
  indicator: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    backgroundColor: theme.colors.blue[4],
  },
}));

function SwitchSummaryFieldElement({
  field,
  value,
  sectionKey,
  allErrors,
  allValues,
  onValueChange,
}: StaticFieldProps<SwitchSummaryFieldType>) {
  const { classes, theme } = useStyles();

  const isDependencySatisfied = useMemo(() => {
    if (!field.dependsOn) return true;

    const dependsOn = field.dependsOn;
    const dependencyValues = getDependenciesValues(allValues, sectionKey, dependsOn);
    const isDependant = getIsDependenciesSatisfied(
      field.key,
      dependsOn,
      allErrors,
      dependencyValues
    );

    return isDependant;
  }, [JSON.stringify(allErrors), JSON.stringify(allValues), field.key, sectionKey]);

  useEffect(() => {
    if (typeof field.defaultValue === 'undefined') return;
    if (typeof value === 'undefined' || !isDependencySatisfied) {
      onValueChange(field?.defaultValue ?? false, true);
    }
  }, [field.defaultValue, isDependencySatisfied]);

  const handleOnChange = useCallback(
    (e) => {
      const { target } = e;
      onValueChange(target.checked);
    },
    [onValueChange]
  );

  return (
    <div className={classes.card}>
      <Group data-test-id={dt.switchOption} className={classes.justify}>
        <Flex align="center" gap={theme.other.spacing._4}>
          {field.indicator && <Box className={classes.indicator} />}
          <Text data-test-id={dt.fieldMeta.title} variant="title-medium">
            {field.label}
          </Text>
        </Flex>
        {field.locked ? (
          <IconLock size={24} stroke={theme.colors.gray[4]} strokeWidth={1.5} />
        ) : (
          <Switch
            size="md"
            disabled={field?.disabled}
            checked={typeof value === 'boolean' ? value : false}
            onChange={handleOnChange}
          />
        )}
      </Group>
    </div>
  );
}

const createValidationSchema = (field: SwitchSummaryFieldType) => {
  let fieldSchema: ZodTypeAny = z.boolean();
  if (field.validation?.isOptional) {
    fieldSchema = fieldSchema.optional();
  }

  return fieldSchema;
};

const Descriptor: FieldDescriptor<SwitchSummaryFieldType> = {
  FieldComponent: SwitchSummaryFieldElement,
  name: 'SwitchSummaryField',
  createValidationSchema,
};

export default Descriptor;
